/**
 * i18n configuration for bilingual EN ↔ ES experience.
 *
 * Strategy:
 *  - Store the user's language preference in cookie `lbp_lang` + localStorage fallback.
 *  - `/es` and `/es/:slug` native routes lock language to `es` regardless of preference
 *    (handled in App-level listener). The toggle still flips preference for the rest
 *    of the site.
 *  - Translations live under `src/i18n/locales/{lang}/{namespace}.json`.
 */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import enCommon from './locales/en/common.json';
import enNav from './locales/en/nav.json';
import enFooter from './locales/en/footer.json';
import enHome from './locales/en/home.json';
import enDashboard from './locales/en/dashboard.json';
import enSignup from './locales/en/signup.json';
import enPartner from './locales/en/partner.json';
import enAuth from './locales/en/auth.json';
import enLegal from './locales/en/legal.json';
import enCompare from './locales/en/compare.json';

import esCommon from './locales/es/common.json';
import esNav from './locales/es/nav.json';
import esFooter from './locales/es/footer.json';
import esHome from './locales/es/home.json';
import esDashboard from './locales/es/dashboard.json';
import esSignup from './locales/es/signup.json';
import esPartner from './locales/es/partner.json';
import esAuth from './locales/es/auth.json';
import esLegal from './locales/es/legal.json';
import esCompare from './locales/es/compare.json';

export const SUPPORTED_LANGUAGES = ['en', 'es'];
export const DEFAULT_LANGUAGE = 'en';
export const LANG_COOKIE = 'lbp_lang';

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { common: enCommon, nav: enNav, footer: enFooter, home: enHome, dashboard: enDashboard, signup: enSignup, partner: enPartner, auth: enAuth, legal: enLegal, compare: enCompare },
      es: { common: esCommon, nav: esNav, footer: esFooter, home: esHome, dashboard: esDashboard, signup: esSignup, partner: esPartner, auth: esAuth, legal: esLegal, compare: esCompare },
    },
    fallbackLng: DEFAULT_LANGUAGE,
    supportedLngs: SUPPORTED_LANGUAGES,
    ns: ['common', 'nav', 'footer', 'home', 'dashboard', 'signup', 'partner', 'auth', 'legal', 'compare'],
    defaultNS: 'common',
    interpolation: { escapeValue: false },
    detection: {
      order: ['cookie', 'localStorage', 'navigator'],
      caches: ['cookie', 'localStorage'],
      lookupCookie: LANG_COOKIE,
      lookupLocalStorage: LANG_COOKIE,
      cookieMinutes: 60 * 24 * 365,
      cookieOptions: { path: '/', sameSite: 'lax' },
    },
    react: { useSuspense: false },
  });

// Keep <html lang="..."> in sync for accessibility + SEO.
const syncHtmlLang = (lng) => {
  try { document.documentElement.lang = (lng || DEFAULT_LANGUAGE).split('-')[0]; } catch (e) { /* noop */ }
};
syncHtmlLang(i18n.language);
i18n.on('languageChanged', syncHtmlLang);

export default i18n;
